import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DeliverableImportTypes } from '@mono/proto-clients';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { actionLabel, DecisionState, DeferredNotice, FieldTable, ProposalActionBar, ProposalEditor, useProposalDecisionActions, WhatThisWillDo, WorkstreamProgressLine } from './proposalShared';
import { workstreamLabel } from './shared';
const { DeliverableImportAction, DeliverableImportDecision, DeliverableFields } = DeliverableImportTypes;
/**
 * PROPOSAL_REVIEW body: the single staged proposal at `payload.proposalId`,
 * resolved from `review` (never from the card payload, per the proto's
 * DeliverableImportCard doc comment). NEEDS_CLARIFICATION never gets a bare
 * Approve — the proto requires those be resolved via revise first — so it
 * offers only the editor, with a sentence explaining why.
 */ export function ProposalReviewCard({ payload, review }) {
    const [editing, setEditing] = useState(false);
    const [deferred, setDeferred] = useState(false);
    const actions = useProposalDecisionActions(payload);
    const proposal = review.proposals.find((p)=>p.id === payload.proposalId);
    if (!proposal) {
        return /*#__PURE__*/ _jsx("div", {
            className: "mt-2 text-sm text-gray-500",
            children: "This proposal is no longer part of the staged import."
        });
    }
    const workstream = review.workstreams.find((w)=>w.key === proposal.workstreamKey);
    const workstreamText = workstream ? workstreamLabel(workstream) : proposal.workstreamLabel;
    const isPending = proposal.decision === DeliverableImportDecision.PENDING;
    const needsClarification = proposal.action === DeliverableImportAction.NEEDS_CLARIFICATION;
    const fields = proposal.proposed ?? new DeliverableFields();
    const header = `${workstreamText} — Change ${proposal.ordinal} of ${workstream?.totalToReview ?? '?'}`;
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.deliverableImport.proposal,
        "data-proposal-id": proposal.id,
        className: "mt-2 space-y-3 text-sm text-gray-700",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "font-medium text-gray-900",
                children: header
            }),
            editing ? /*#__PURE__*/ _jsx(ProposalEditor, {
                original: fields,
                needsClarification: needsClarification,
                onCancel: ()=>setEditing(false),
                onSave: (revised, reason, resolvedAction)=>{
                    actions.save(proposal.id, proposal.workstreamKey, revised, reason, resolvedAction).then(()=>setEditing(false));
                }
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-sm font-semibold text-gray-800",
                        children: actionLabel(proposal.action)
                    }),
                    needsClarification && /*#__PURE__*/ _jsxs("div", {
                        className: "border-l-2 border-amber-300 pl-2 text-xs text-gray-700 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                children: proposal.clarificationQuestion
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-gray-500",
                                children: "This can't be approved as-is — the ambiguity has to be resolved via the editor first."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(FieldTable, {
                        proposed: fields,
                        existing: proposal.action === DeliverableImportAction.UPDATE ? proposal.existing : undefined,
                        changedFields: proposal.changedFields,
                        workstreamText: workstreamText
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-xs text-gray-600",
                        children: [
                            "Source: ",
                            proposal.source?.sheetName || 'Unknown sheet',
                            " — row",
                            (proposal.source?.rows.length ?? 0) === 1 ? '' : 's',
                            ' ',
                            proposal.source?.rows.join(', ') || '—'
                        ]
                    }),
                    proposal.action !== DeliverableImportAction.ALREADY_MATCHES && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xs font-medium text-gray-600 mb-1",
                                children: "What this will do"
                            }),
                            /*#__PURE__*/ _jsx(WhatThisWillDo, {
                                fields: fields
                            })
                        ]
                    }),
                    isPending ? deferred ? /*#__PURE__*/ _jsx(DeferredNotice, {}) : /*#__PURE__*/ _jsx(ProposalActionBar, {
                        needsClarification: needsClarification,
                        approveLabel: "Approve",
                        approveTestid: Testids.deliverableImport.approve,
                        editLabel: "Edit",
                        editTestid: Testids.deliverableImport.edit,
                        disabled: actions.isSubmitting,
                        onApprove: ()=>actions.approve(proposal.id, proposal.workstreamKey),
                        onEdit: ()=>setEditing(true),
                        onReject: (reason)=>actions.reject(proposal.id, proposal.workstreamKey, reason),
                        onDecideLater: ()=>actions.defer(proposal.id, proposal.workstreamKey).then(()=>setDeferred(true))
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(DecisionState, {
                                proposal: proposal
                            }),
                            /*#__PURE__*/ _jsx(WorkstreamProgressLine, {
                                workstream: workstream
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
